import React, { useState } from "react";
import { Box, Grid, Typography, Paper, Tooltip, Chip, Stack } from "@mui/material";
import { toast } from "react-toastify";
import SteeringWheelIcon from  '../../Assets/BusBookingImg/BusSteeringWheelIcon.png';
import { SeaterIcon, SleeperIcon, SeatCumSleeperIcon } from "../../../res/Services/BusBooking";

const BusLayout = ({ seatData, onSeatSelect }) => {
  const [layout, setLayout] = useState(seatData);
  const [selectedSeats, setSelectedSeats] = useState([]);

  const handleSeatClick = (seat) => {
    const isSelected = selectedSeats.includes(seat.SeatIndex);

    if (isSelected) {
      setSelectedSeats((prev) => prev.filter((id) => id !== seat.SeatIndex));
      toast.info(`Seat ${seat.SeatName} deselected`);
    } else {
      if (selectedSeats.length >= 6) {
        toast.warning("You can select a maximum of 6 seats.");
        return;
      }

      setSelectedSeats((prev) => [...prev, seat.SeatIndex]);
      toast.info(`Seat ${seat.SeatName} selected`);
    }

    if (onSeatSelect) {
      onSeatSelect(seat);
    }
  };

  const getSeatColor = (seat) => {
    if (selectedSeats.includes(seat.SeatIndex)) {
      return "#4CAF50"; // Selected seat color (green)
    }
    if (!seat.SeatStatus) {
      return "#F44336"; // Booked seat color (red)
    }
    if (seat.IsLadiesSeat) {
      return "#E91E63"; // Available for female (pink)
    }
    if (seat.IsMalesSeat) {
      return "#2196F3"; // Available for male (blue)
    }

  };

  const renderSeatPrice = (price) => {
    return (
      <>
        <Typography variant="body2">Price: ₹{price.BasePrice}</Typography>
      </>
    );
  };

  const renderSeatIcon = (seat) => {
    const iconStyle = {
      width: seat.SeatType === 2 ? "50px" : "35px",
      height: seat.SeatType === 2 ? "25px" : "35px",
      opacity: seat.SeatStatus ? 1 : 0.5,
    };

    switch (seat.SeatType) {
      case 1:
        return <SeaterIcon style={iconStyle} />;
      case 2:
        return <SleeperIcon style={iconStyle} />;
      case 3:
        return <SeatCumSleeperIcon style={iconStyle} />;
      default:
        return null;
    }
  };

  const renderSeats = (seats, isLowerDeck = false) => {
    const maxRow = Math.max(...seats.map((seat) => parseInt(seat.RowNo, 10)));
    const maxCol = Math.max(...seats.map((seat) => parseInt(seat.ColumnNo, 10)));
  
    const grid = Array.from({ length: maxRow + 1 }, () =>
      Array.from({ length: maxCol + 1 }, () => null)
    );
  
    seats.forEach((seat) => {
      const rowIndex = parseInt(seat.RowNo, 10);
      const colIndex = parseInt(seat.ColumnNo, 10);
  
      if (rowIndex >= 0 && rowIndex <= maxRow && colIndex >= 0 && colIndex <= maxCol) {
        grid[rowIndex][colIndex] = seat;
      }
    });
  
    return grid.map((row, rowIndex) => (
      <Grid 
        container 
        spacing={2}
        key={rowIndex} 
        sx={{ 
          marginBottom: 1,
          '& .MuiGrid-item': {
            padding: '4px !important'
          }
        }}
      >
        {row.map((seat, colIndex) => (
          <Grid 
            item 
            key={colIndex} 
            xs={12 / (maxCol + 1)}
            sx={{
              padding: seat?.SeatType === 2 ? '2px !important' : '4px !important'
            }}
          >
            {seat ? (
              <Tooltip
                title={
                  <>
                    <Typography variant="body1" fontWeight="bold">
                      {seat.SeatName}
                    </Typography>
                    {renderSeatPrice(seat.Price)}
                    <Typography variant="caption" display="block">
                      {!seat.SeatStatus
                        ? "Booked"
                        : seat.IsLadiesSeat
                        ? "Ladies Seat"
                        : seat.IsMalesSeat
                        ? "Male Seat"
                        : "Available"}
                    </Typography>
                  </>
                }
                arrow
              >
                <Box
                  onClick={() => seat.SeatStatus && handleSeatClick(seat)}
                  sx={{
                    textAlign: "center",
                    backgroundColor: getSeatColor(seat),
                    cursor: seat.SeatStatus ? "pointer" : "not-allowed",
                    width: seat.SeatType === 2 ? "50px" : "35px",
                    height: seat.SeatType === 2 ? "25px" : "35px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                    borderRadius: seat.SeatType === 2 ? "4px" : "8px",
                    margin: "0 auto",
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: seat.SeatStatus ? "scale(1.05)" : "none",
                    },
                  }}
                >
                  {renderSeatIcon(seat)}
                  <Typography
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      fontSize: seat.SeatType === 2 ? '10px' : '12px',
                      transform: "translate(-50%, -50%)",
                      color: selectedSeats.includes(seat.SeatIndex) 
                        ? "white" 
                        : !seat.SeatStatus 
                          ? "white" 
                          : "black",
                      fontWeight: "bold",
                      zIndex: 1,
                    }}
                  >
                    {seat.SeatName}
                  </Typography>
                </Box>
              </Tooltip>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  padding: 1,
                  textAlign: "center",
                  backgroundColor: "transparent",
                }}
              />
            )}
          </Grid>
        ))}
      </Grid>
    ));
  };

  const renderDecks = () => {
    const seats = layout.SeatLayout.SeatDetails.flat();
    const upperDeck = seats.filter((seat) => seat.IsUpper);
    const lowerDeck = seats.filter((seat) => !seat.IsUpper);

    return (
      <>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip label="Available" sx={{color: "black" }} />
          <Chip label="Booked" sx={{ backgroundColor: "#F44336", color: "white" }} />
          <Chip label="Selected" sx={{ backgroundColor: "#4CAF50", color: "white" }} />
          <Chip label="Ladies Seat" sx={{ backgroundColor: "#E91E63", color: "white" }} />
          <Chip label="Male Seat" sx={{ backgroundColor: "#2196F3", color: "white" }} />
        </Stack>

        {upperDeck.length > 0 && (
          <Paper sx={{ padding: "1rem", boxShadow: 3, borderRadius: "12px", marginBottom: "4%" }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 2 }} gutterBottom>
              Upper Deck
            </Typography>
            {renderSeats(upperDeck)}
          </Paper>
        )}

        <Paper sx={{ 
          padding: "1rem", 
          boxShadow: 3, 
          borderRadius: "12px", 
          position: "relative",
          overflow: 'visible' // Allow the icon to overflow
        }}>
          <Box sx={{
      position: 'absolute',
      left: -40,
      top: 35,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      <img 
        src={SteeringWheelIcon} 
        alt="Steering Wheel" 
        style={{ 
          transform: 'rotate(90deg)',
          color: 'text.secondary',
          fontSize: '2rem',
          width: '32px', // Adjust size as needed
          height: '32px' // Adjust size as needed
        }} 
      />
      <Typography variant="caption" color="text.secondary">
        Driver
      </Typography>
    </Box>
          
          {upperDeck.length > 0 && (
  <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 2 }} gutterBottom>
    Lower Deck
  </Typography>
)}
          {renderSeats(lowerDeck, true)}
        </Paper>
      </>
    );
  };

  return <Box sx={{ padding: 2 }}>{renderDecks()}</Box>;
};

export default BusLayout;